// ponytail: [cynos-sociomanch payment bridge return handler]
import api from "../lib/api";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/utils-cynos";
import { verifyPhonePePayment } from "../services/paymentService";

function buildReturnUrl(base, params) {
  try {
    const url = new URL(base, window.location.origin);
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    });
    return url.toString();
  } catch {
    const sep = base.includes("?") ? "&" : "?";
    const q = Object.entries(params)
      .map(([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(String(v || "")))
      .join("&");
    return base + sep + q;
  }
}

export default function PaymentReturn() {
  const location = useLocation();
  const { clear } = useCart();
  const [state, setState] = useState({ loading: true, error: "", order: null });

  const params = new URLSearchParams(location.search);
  const merchantTransactionId =
    params.get("merchant_transaction_id") ||
    params.get("transactionId") ||
    params.get("merchantTransactionId") ||
    params.get("merchantOrderId");
  const returnUrlParam = params.get("return_url") || params.get("returnUrl");
  const plan = params.get("plan") || "growth";
  const code = params.get("code") || "";

  // Identify SocioManch payments via return_url param or SM- transaction prefix
  const isSocioManch = Boolean(
    returnUrlParam || (merchantTransactionId && merchantTransactionId.startsWith("SM-"))
  );
  const effectiveReturnUrl = returnUrlParam || (isSocioManch ? "https://sociomanch.com/billing" : null);

  useEffect(() => {
    // ── SocioManch External Bridge Handler ──────────────────────────────────
    if (isSocioManch && effectiveReturnUrl) {
      const isExplicitCancel =
        !merchantTransactionId ||
        code === "PAYMENT_CANCELLED" ||
        code === "PAYMENT_ERROR" ||
        code === "PAYMENT_DECLINED" ||
        code === "USER_CANCEL";

      if (isExplicitCancel) {
        setState({
          loading: false,
          error: "Payment was cancelled. Returning to SocioManch...",
          order: null,
        });
        const target = buildReturnUrl(effectiveReturnUrl, {
          payment: "cancelled",
          status: "cancelled",
          error: "Payment was cancelled",
          plan,
        });
        const timer = setTimeout(() => {
          window.location.href = target;
        }, 1200);
        return () => clearTimeout(timer);
      }

      // Has transaction ID -> query Cynos bridge status for PhonePe result
      api
        .get(`/payments/bridge/status/${merchantTransactionId}`)
        .then((res) => {
          const data = res.data;
          const isSuccess =
            data.payment_status === "success" ||
            data.status === "confirmed" ||
            data.status === "COMPLETED";

          setState({
            loading: false,
            error: isSuccess ? "" : "Payment was not completed or failed.",
            order: data,
          });

          const target = isSuccess
            ? buildReturnUrl(effectiveReturnUrl, {
                payment: "success",
                status: "success",
                txn_id: merchantTransactionId,
                plan,
              })
            : buildReturnUrl(effectiveReturnUrl, {
                payment: "failed",
                status: data.payment_status || "failed",
                txn_id: merchantTransactionId,
                plan,
              });

          const timer = setTimeout(() => {
            window.location.href = target;
          }, 1200);
          return () => clearTimeout(timer);
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail || "Payment verification failed.";
          setState({ loading: false, error: detail, order: null });
          const target = buildReturnUrl(effectiveReturnUrl, {
            payment: "failed",
            status: "failed",
            error: detail,
            plan,
          });
          const timer = setTimeout(() => {
            window.location.href = target;
          }, 1200);
          return () => clearTimeout(timer);
        });
      return;
    }

    // ── Standard Cynos E-Commerce Flow (100% untouched for Cynos store) ──────
    if (!merchantTransactionId) {
      setState({ loading: false, error: "Payment reference is missing.", order: null });
      return;
    }

    verifyPhonePePayment(merchantTransactionId)
      .then((data) => {
        if (data.payment_status === "success") clear();
        setState({ loading: false, error: "", order: data });
      })
      .catch((err) => {
        const detail =
          err?.response?.data?.detail ||
          "Could not verify payment. Please contact support if money was deducted.";
        setState({ loading: false, error: detail, order: null });
      });
  }, [clear, location.search, merchantTransactionId, isSocioManch, effectiveReturnUrl, plan, code]);

  const order = state.order;
  const success = order?.payment_status === "success" || order?.status === "confirmed";
  const pending = order?.payment_status === "pending" || order?.payment_status === "payment_pending";

  const cancelReturnHref = effectiveReturnUrl
    ? buildReturnUrl(effectiveReturnUrl, { payment: "cancelled", status: "cancelled", plan })
    : "/checkout";

  const successReturnHref = effectiveReturnUrl
    ? buildReturnUrl(effectiveReturnUrl, { payment: "success", status: "success", txn_id: merchantTransactionId || "", plan })
    : "/checkout";

  return (
    <>
      {/* Override Cynos body background - user must not see Cynos branding */}
      <style>{`
        body { margin: 0 !important; padding: 0 !important; background: #000 !important; }
        #root > div.App { min-height: 100vh; background: #000; }
      `}</style>
      <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8 flex items-center justify-center font-sans">
      <div className="mx-auto max-w-xl w-full border border-white/10 bg-cynos-card p-8 text-center text-white">
        {state.loading ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-cynos-red" />
            <h1 className="mt-6 font-anton text-4xl uppercase tracking-wider">Verifying Payment.</h1>
            <p className="mt-3 text-sm text-zinc-400">Checking the latest PhonePe status.</p>
          </>
        ) : state.error ? (
          <>
            <ShieldAlert className="mx-auto h-12 w-12 text-cynos-red mb-2" />
            <h1 className="mt-4 font-anton text-3xl uppercase tracking-wider">
              {isSocioManch ? "Payment Incomplete / Cancelled" : "Payment Check Failed."}
            </h1>
            <p className="mt-3 text-sm text-zinc-400">{state.error}</p>
            {isSocioManch && (
              <p className="mt-2 text-xs text-indigo-400">Redirecting back to SocioManch...</p>
            )}
            <div className="mt-8 flex justify-center gap-3">
              {isSocioManch ? (
                <a
                  href={cancelReturnHref}
                  className="inline-block bg-white px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors"
                >
                  Return to SocioManch
                </a>
              ) : (
                <Link
                  to="/checkout"
                  className="inline-block bg-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black"
                >
                  Back to Checkout
                </Link>
              )}
            </div>
          </>
        ) : (
          <>
            {success ? (
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            ) : (
              <ShieldAlert className="mx-auto h-14 w-14 text-cynos-red" />
            )}
            <h1 className="mt-6 font-anton text-4xl uppercase tracking-wider">
              {success ? "Payment Successful." : pending ? "Payment Pending." : "Payment Failed."}
            </h1>
            {isSocioManch ? (
              <>
                <p className="mt-3 text-sm text-zinc-300">
                  SocioManch Plan: <span className="font-bold text-white uppercase">{plan}</span>
                </p>
                <p className="mt-2 text-xs text-indigo-400">
                  Redirecting back to SocioManch dashboard...
                </p>
                <div className="mt-8 flex justify-center">
                  <a
                    href={successReturnHref}
                    className="inline-block bg-white px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors"
                  >
                    Return to SocioManch
                  </a>
                </div>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-zinc-400">
                  Order {order?.id?.slice(0, 8)?.toUpperCase()} • {formatINR(order?.total || 0)}
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Status: {order?.status?.replaceAll("_", " ")}
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <Link
                    to={`/order/${order?.id}`}
                    className="bg-cynos-red px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white"
                  >
                    View Order
                  </Link>
                  {!success && (
                    <Link
                      to="/checkout"
                      className="border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white"
                    >
                      Try Again
                    </Link>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </main>
    </>
  );
}
